/**
 * Response Unit Service
 * Database operations and content management for Response Units
 * Integrates with existing drills, scripts, objections, and micro-lessons
 */

import { supabase } from '../lib/supabase';
import { validateResponseUnit, convertLegacyToResponseUnit } from './responseUnitUtils';
import { quickHybridSearch, hybridRetrieve } from './hybridRetrieval';

export const responseUnitService = {
  /**
   * Create a new Response Unit
   * @param {Object} unit - Response Unit to create
   * @returns {Promise<Object>} Created unit with ID
   */
  async createResponseUnit(unit) {
    try {
      // Validate before saving
      const validation = validateResponseUnit(unit);
      
      if (!validation.isValid) {
        throw new Error(`Invalid Response Unit: ${validation.issues.map(i => i.message).join(', ')}`);
      }

      const { data, error } = await supabase
        .from('response_units')
        .insert([{
          ...unit,
          quality_score: validation.score,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error creating Response Unit:', error);
      throw error;
    }
  },

  /**
   * Get Response Unit by ID
   * @param {string} id - Unit ID
   * @returns {Promise<Object>} Response Unit
   */
  async getResponseUnit(id) {
    try {
      const { data, error } = await supabase
        .from('response_units')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching Response Unit:', error);
      throw error;
    }
  },

  /**
   * Get all Response Units with filters
   * @param {Object} filters - Filter criteria
   * @returns {Promise<Array>} Array of Response Units
   */
  async getResponseUnits(filters = {}) {
    try {
      let query = supabase
        .from('response_units')
        .select('*')
        .order('quality_score', { ascending: false });

      // Apply filters
      if (filters.type) {
        query = query.eq('type', filters.type);
      }

      if (filters.difficulty) {
        query = query.eq('metadata->>difficulty', filters.difficulty);
      }

      if (filters.persona) {
        query = query.eq('metadata->>persona', filters.persona);
      }

      if (filters.industry && filters.industry !== 'general') {
        query = query.or(`metadata->>industry.eq.${filters.industry},metadata->>industry.eq.general`);
      }

      if (filters.minQualityScore) {
        query = query.gte('quality_score', filters.minQualityScore);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching Response Units:', error);
      throw error;
    }
  },

  /**
   * Update Response Unit
   * @param {string} id - Unit ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated unit
   */
  async updateResponseUnit(id, updates) {
    try {
      // Re-validate if phases changed
      if (updates.phases) {
        const fullUnit = { ...updates, id };
        const validation = validateResponseUnit(fullUnit);
        updates.quality_score = validation.score;
      }

      const { data, error } = await supabase
        .from('response_units')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating Response Unit:', error);
      throw error;
    }
  },

  /**
   * Delete Response Unit
   * @param {string} id - Unit ID
   * @returns {Promise<void>}
   */
  async deleteResponseUnit(id) {
    try {
      const { error } = await supabase
        .from('response_units')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting Response Unit:', error);
      throw error;
    }
  },

  /**
   * Search Response Units by text
   * @param {string} query - Search query
   * @param {Object} filters - Additional filters
   * @returns {Promise<Array>} Matching units
   */
  async searchResponseUnits(query, filters = {}) {
    try {
      let dbQuery = supabase
        .from('response_units')
        .select('*');

      // Full-text search on title and phases
      if (query) {
        dbQuery = dbQuery.or(
          `title.ilike.%${query}%,` +
          `phases->>acknowledge.ilike.%${query}%,` +
          `phases->>value.ilike.%${query}%,` +
          `tags.cs.{${query}}`
        );
      }

      // Apply additional filters
      if (filters.type) {
        dbQuery = dbQuery.eq('type', filters.type);
      }

      if (filters.minQualityScore) {
        dbQuery = dbQuery.gte('quality_score', filters.minQualityScore);
      }

      dbQuery = dbQuery.order('quality_score', { ascending: false }).limit(20);

      const { data, error } = await dbQuery;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching Response Units:', error);
      throw error;
    }
  },

  /**
   * Get Response Unit for specific objection using hybrid retrieval
   * @param {string} objectionType - Objection type
   * @param {Object} context - User context (industry, level, persona, stage)
   * @returns {Promise<Object>} Best matching Response Unit with retrieval metadata
   */
  async getObjectionResponse(objectionType, context = {}) {
    try {
      // Use hybrid retrieval for intelligent context-aware matching
      const result = await quickHybridSearch(
        objectionType,
        'objection',
        {
          persona: context.persona,
          stage: context.stage || 'discovery',
          industry: context.industry || 'general'
        }
      );

      // Return the response unit with retrieval metadata
      return {
        ...result.responseUnit,
        retrievalMethod: result.retrievalMethod,
        retrievalScore: result.scores.combined,
        scoreBreakdown: result.scoreBreakdown
      };
    } catch (error) {
      console.error('Error getting objection response:', error);
      
      // Fallback to basic search if hybrid retrieval fails
      const filters = {
        type: 'objection',
        difficulty: context.difficulty || 'intermediate',
        industry: context.industry || 'general',
        minQualityScore: 80
      };
      
      const units = await this.searchResponseUnits(objectionType, filters);
      return units[0] || null;
    }
  },

  /**
   * Get script template by type
   * @param {string} scriptType - Script type (cold_call, email, follow_up, etc.)
   * @param {Object} context - User context
   * @returns {Promise<Object>} Script Response Unit
   */
  async getScriptTemplate(scriptType, context = {}) {
    try {
      const filters = {
        type: 'script',
        difficulty: context.difficulty || 'beginner',
        industry: context.industry || 'general',
        minQualityScore: 80
      };

      const units = await this.searchResponseUnits(scriptType, filters);

      if (units.length === 0) {
        throw new Error(`No script template found for: ${scriptType}`);
      }

      return units[0];
    } catch (error) {
      console.error('Error getting script template:', error);
      throw error;
    }
  },

  /**
   * Get micro-lesson by skill gap
   * @param {string} skillGap - Identified weakness
   * @param {Object} context - User context
   * @returns {Promise<Object>} Micro-lesson Response Unit
   */
  async getMicroLesson(skillGap, context = {}) {
    try {
      const filters = {
        type: 'micro_lesson',
        difficulty: context.difficulty || 'beginner',
        minQualityScore: 85
      };

      const units = await this.searchResponseUnits(skillGap, filters);

      if (units.length === 0) {
        return null;
      }

      return units[0];
    } catch (error) {
      console.error('Error getting micro-lesson:', error);
      throw error;
    }
  },

  /**
   * Migrate legacy content to Response Units
   * @param {string} contentType - Type of content to migrate
   * @returns {Promise<Object>} Migration results
   */
  async migrateLegacyContent(contentType = 'objection') {
    try {
      let legacyTable = '';
      let convertType = '';

      switch (contentType) {
        case 'objection':
          legacyTable = 'objection_library';
          convertType = 'objection';
          break;
        case 'script':
          legacyTable = 'script_library';
          convertType = 'script';
          break;
        default:
          throw new Error(`Unknown content type: ${contentType}`);
      }

      // Fetch legacy content
      const { data: legacyContent, error: fetchError } = await supabase
        .from(legacyTable)
        .select('*')
        .limit(100);

      if (fetchError) throw fetchError;

      const results = {
        total: legacyContent.length,
        migrated: 0,
        failed: 0,
        errors: []
      };

      // Convert and save each item
      for (const item of legacyContent) {
        try {
          const responseUnit = convertLegacyToResponseUnit(item, convertType);
          
          if (responseUnit) {
            await this.createResponseUnit(responseUnit);
            results.migrated++;
          }
        } catch (error) {
          results.failed++;
          results.errors.push({
            id: item.id,
            error: error.message
          });
        }
      }

      return results;
    } catch (error) {
      console.error('Error migrating legacy content:', error);
      throw error;
    }
  },

  /**
   * Get recommended Response Unit for drill feedback
   * @param {Object} drillResult - Drill performance data
   * @returns {Promise<Object>} Recommended hint or micro-lesson
   */
  async getRecommendedFeedback(drillResult) {
    try {
      const { score, weaknesses, difficulty } = drillResult;

      // If score is low, provide targeted hint
      if (score < 60) {
        const primaryWeakness = weaknesses?.[0] || 'general';
        
        // Try to find specific hint
        const hint = await this.getMicroLesson(primaryWeakness, { difficulty });
        
        if (hint) return hint;
      }

      // Otherwise, return encouraging micro-lesson
      const lesson = await this.searchResponseUnits('improvement tips', {
        type: 'micro_lesson',
        minQualityScore: 85
      });

      return lesson[0] || null;
    } catch (error) {
      console.error('Error getting recommended feedback:', error);
      return null;
    }
  }
};

export default responseUnitService;
